import { useId } from "react";

const DEFAULT_TABLE_COLOR = "#c49a6c";

const PALETTES = {
  available: {
    woodLight: "#e8c89a",
    woodMid: "#c49a6c",
    woodDark: "#9a7348",
    woodEdge: "#6b4f2a",
    seat: "#5c6578",
    seatDark: "#3f4654",
    cushion: "#64748b",
    accent: "#059669",
  },
  booked: {
    woodLight: "#efb8b8",
    woodMid: "#d48484",
    woodDark: "#b85c5c",
    woodEdge: "#8f3f3f",
    seat: "#7f1d1d",
    seatDark: "#5b1515",
    cushion: "#991b1b",
    accent: "#dc2626",
  },
  tooSmall: {
    woodLight: "#f0d08a",
    woodMid: "#d4a84a",
    woodDark: "#b8860b",
    woodEdge: "#92650a",
    seat: "#78716c",
    seatDark: "#57534e",
    cushion: "#a16207",
    accent: "#d97706",
  },
  unknown: {
    woodLight: "#f0d08a",
    woodMid: "#d4a84a",
    woodDark: "#b8860b",
    woodEdge: "#92650a",
    seat: "#78716c",
    seatDark: "#57534e",
    cushion: "#a16207",
    accent: "#d97706",
  },
};

const STATUS_TINT = {
  available: null,
  booked: "#dc2626",
  tooSmall: "#d97706",
  unknown: "#d97706",
};

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const parseHexColor = (hex) => {
  if (hex == null || typeof hex !== "string") return null;
  let value = hex.trim();
  if (!value.startsWith("#")) value = `#${value}`;
  if (/^#[0-9A-F]{3}$/i.test(value)) {
    value = `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  if (!/^#[0-9A-F]{6}$/i.test(value)) return null;
  return {
    r: parseInt(value.slice(1, 3), 16),
    g: parseInt(value.slice(3, 5), 16),
    b: parseInt(value.slice(5, 7), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, "0"))
    .join("")}`;

const mixRgb = (from, to, amount) => ({
  r: from.r + (to.r - from.r) * amount,
  g: from.g + (to.g - from.g) * amount,
  b: from.b + (to.b - from.b) * amount,
});

const lightenRgb = (rgb, amount) =>
  mixRgb(rgb, { r: 255, g: 255, b: 255 }, amount);

const darkenRgb = (rgb, amount) =>
  mixRgb(rgb, { r: 0, g: 0, b: 0 }, amount);

const buildPaletteFromBase = (baseColor, status = "available") => {
  const fallback = PALETTES[status] || PALETTES.available;
  const parsed = parseHexColor(baseColor);
  if (!parsed) return fallback;

  const tintHex = STATUS_TINT[status];
  const tinted = tintHex
    ? mixRgb(parsed, parseHexColor(tintHex) || parsed, 0.42)
    : parsed;

  return {
    woodLight: rgbToHex(lightenRgb(tinted, 0.28)),
    woodMid: rgbToHex(tinted),
    woodDark: rgbToHex(darkenRgb(tinted, 0.22)),
    woodEdge: rgbToHex(darkenRgb(tinted, 0.42)),
    seat: fallback.seat,
    seatDark: fallback.seatDark,
    cushion: fallback.cushion,
    accent: fallback.accent,
  };
};

const resolvePalette = (status, baseColor) => {
  if (baseColor && parseHexColor(baseColor)) {
    return buildPaletteFromBase(baseColor, status);
  }
  return PALETTES[status] || PALETTES.available;
};

const VIEWBOX = {
  round: "0 0 88 88",
  square: "0 0 92 92",
  rectangle: "0 0 132 82",
  oval: "0 0 138 86",
  booth: "0 0 128 92",
  bar: "0 0 156 58",
};

const DEFAULT_SEATS = {
  round: 2,
  square: 4,
  rectangle: 6,
  oval: 6,
  booth: 4,
  bar: 4,
};

const normalizeSeats = (seats, shape) => {
  const fallback = DEFAULT_SEATS[shape] || 2;
  const value = Number(seats);
  if (!Number.isFinite(value) || value < 1) return fallback;
  return Math.min(12, Math.round(value));
};

const distributeOnLine = (count, start, end) => {
  if (count <= 0) return [];
  if (count === 1) return [(start + end) / 2];
  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + step * index);
};

const chairAngleToward = (x, y, cx, cy) =>
  (Math.atan2(x - cx, y - cy) * 180) / Math.PI;

const seatsOnCircle = (count, cx, cy, radius) =>
  Array.from({ length: count }, (_, index) => {
    const radians = ((360 / count) * index - 90) * (Math.PI / 180);
    const x = cx + radius * Math.cos(radians);
    const y = cy + radius * Math.sin(radians);
    return { x, y, angle: chairAngleToward(x, y, cx, cy) };
  });

const seatsOnEllipse = (count, cx, cy, rx, ry) =>
  Array.from({ length: count }, (_, index) => {
    const radians = ((360 / count) * index - 90) * (Math.PI / 180);
    const x = cx + rx * Math.cos(radians);
    const y = cy + ry * Math.sin(radians);
    return { x, y, angle: chairAngleToward(x, y, cx, cy) };
  });

const seatsOnSquare = (count, cx, cy, halfSize, edgeInset) => {
  const sides = [
    { x: cx, y: cy - halfSize - edgeInset, angle: 0 },
    { x: cx + halfSize + edgeInset, y: cy, angle: 90 },
    { x: cx, y: cy + halfSize + edgeInset, angle: 180 },
    { x: cx - halfSize - edgeInset, y: cy, angle: -90 },
  ];

  if (count <= 4) {
    return sides.slice(0, count);
  }

  const left = cx - halfSize;
  const right = cx + halfSize;
  const top = cy - halfSize;
  const bottom = cy + halfSize;
  const perimeter = 2 * (halfSize * 2 + halfSize * 2);

  return Array.from({ length: count }, (_, index) => {
    const distance = (perimeter * index) / count;
    const width = halfSize * 2;
    const height = halfSize * 2;

    if (distance <= width) {
      const x = left + distance;
      return { x, y: top - edgeInset, angle: 0 };
    }
    if (distance <= width + height) {
      const y = top + (distance - width);
      return { x: right + edgeInset, y, angle: 90 };
    }
    if (distance <= width * 2 + height) {
      const x = right - (distance - width - height);
      return { x, y: bottom + edgeInset, angle: 180 };
    }
    const y = bottom - (distance - width * 2 - height);
    return { x: left - edgeInset, y, angle: -90 };
  });
};

const seatsOnLongSides = (count, left, right, topY, bottomY) => {
  const topCount = Math.ceil(count / 2);
  const bottomCount = count - topCount;
  const topXs = distributeOnLine(topCount, left, right);
  const bottomXs = distributeOnLine(bottomCount, left, right);

  return [
    ...topXs.map((x) => ({ x, y: topY, angle: 0 })),
    ...bottomXs.map((x) => ({ x, y: bottomY, angle: 180 })),
  ];
};

const placeSettingsInRect = (count, left, top, width, height) => {
  if (count <= 0) return [];

  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  const cellW = width / (cols + 1);
  const cellH = height / (rows + 1);

  return Array.from({ length: count }, (_, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;
    const itemsInRow = Math.min(cols, count - row * cols);
    const rowOffset = ((cols - itemsInRow) * cellW) / 2;

    return {
      x: left + rowOffset + cellW * (col + 1),
      y: top + cellH * (row + 1),
    };
  });
};

const Chair = ({ x, y, angle = 0, palette, scale = 1 }) => {
  const seatW = 14 * scale;
  const seatH = 10 * scale;
  const backH = 5 * scale;

  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`}>
      <rect
        x={-seatW / 2}
        y={-seatH / 2 - backH}
        width={seatW}
        height={backH}
        rx={2 * scale}
        fill={palette.seatDark}
      />
      <rect
        x={-seatW / 2}
        y={-seatH / 2}
        width={seatW}
        height={seatH}
        rx={3 * scale}
        fill={palette.seat}
        stroke={palette.seatDark}
        strokeWidth={0.8 * scale}
      />
    </g>
  );
};

const Stool = ({ x, y, palette }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle cx={0} cy={0} r={5.5} fill={palette.seatDark} />
    <circle cx={0} cy={0} r={4.2} fill={palette.seat} stroke={palette.seatDark} strokeWidth={0.7} />
    <rect x={-1.2} y={4.5} width={2.4} height={3.5} rx={0.8} fill={palette.seatDark} />
  </g>
);

const Bench = ({ x, y, width, palette, flip = false, seatCount = 0 }) => {
  const seatMarkers = distributeOnLine(seatCount, 8, width - 8);

  return (
    <g transform={`translate(${x} ${y})${flip ? " scale(1,-1)" : ""}`}>
      <rect
        x={0}
        y={0}
        width={width}
        height={10}
        rx={3}
        fill={palette.cushion}
        stroke={palette.seatDark}
        strokeWidth={0.8}
      />
      <rect
        x={1.5}
        y={1.5}
        width={width - 3}
        height={7}
        rx={2}
        fill={palette.seat}
        opacity={0.55}
      />
      <rect x={2} y={10} width={width - 4} height={4} rx={1} fill={palette.seatDark} />
      {seatMarkers.map((markerX, index) => (
        <circle
          key={index}
          cx={markerX}
          cy={5}
          r={2.2}
          fill={palette.seatDark}
          opacity={0.75}
        />
      ))}
    </g>
  );
};

const PlaceSetting = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <circle cx={0} cy={0} r={3.4} fill="#fffdf8" stroke="#d6d3d1" strokeWidth={0.65} />
    <circle cx={0} cy={0} r={1.15} fill="#e7e5e4" />
  </g>
);

const WoodDefs = ({ id, palette }) => (
  <defs>
    <linearGradient id={`wood-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stopColor={palette.woodLight} />
      <stop offset="48%" stopColor={palette.woodMid} />
      <stop offset="100%" stopColor={palette.woodDark} />
    </linearGradient>
    <pattern
      id={`grain-${id}`}
      width="6"
      height="6"
      patternUnits="userSpaceOnUse"
      patternTransform="rotate(12)"
    >
      <line x1="0" y1="0" x2="0" y2="6" stroke={palette.woodEdge} strokeWidth="0.35" opacity="0.18" />
    </pattern>
    <filter id={`shadow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" floodColor="#0f172a" floodOpacity="0.22" />
    </filter>
  </defs>
);

const RoundTable = ({ palette, id, seats }) => {
  const chairs = seatsOnCircle(seats, 44, 44, 32);
  const settings = placeSettingsInRect(seats, 30, 30, 28, 28);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      {chairs.map((chair, index) => (
        <Chair key={index} {...chair} palette={palette} scale={seats > 6 ? 0.82 : 1} />
      ))}
      <g filter={`url(#shadow-${id})`}>
        <circle cx={44} cy={44} r={22} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <circle cx={44} cy={44} r={22} fill={`url(#grain-${id})`} stroke="none" />
        <circle cx={44} cy={40} r={3.5} fill={palette.woodLight} opacity={0.35} />
        <circle cx={44} cy={44} r={2.2} fill={palette.woodEdge} opacity={0.45} />
      </g>
      {settings.map((spot, index) => (
        <PlaceSetting key={index} x={spot.x} y={spot.y} scale={seats > 4 ? 0.78 : 0.95} />
      ))}
    </>
  );
};

const SquareTable = ({ palette, id, seats }) => {
  const chairs = seatsOnSquare(seats, 46, 46, 22, 10);
  const settings = placeSettingsInRect(seats, 28, 28, 36, 36);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      {chairs.map((chair, index) => (
        <Chair key={index} {...chair} palette={palette} scale={seats > 6 ? 0.82 : 1} />
      ))}
      <g filter={`url(#shadow-${id})`}>
        <rect x={24} y={24} width={44} height={44} rx={4} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <rect x={24} y={24} width={44} height={44} rx={4} fill={`url(#grain-${id})`} stroke="none" />
      </g>
      {settings.map((spot, index) => (
        <PlaceSetting key={index} x={spot.x} y={spot.y} scale={seats > 4 ? 0.82 : 1} />
      ))}
    </>
  );
};

const RectangleTable = ({ palette, id, seats }) => {
  const chairs = seatsOnLongSides(seats, 28, 104, 9, 73);
  const settings = placeSettingsInRect(seats, 24, 28, 84, 26);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      {chairs.map((chair, index) => (
        <Chair key={index} {...chair} palette={palette} scale={seats > 6 ? 0.82 : 0.92} />
      ))}
      <g filter={`url(#shadow-${id})`}>
        <rect x={18} y={24} width={96} height={34} rx={4} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <rect x={18} y={24} width={96} height={34} rx={4} fill={`url(#grain-${id})`} stroke="none" />
      </g>
      {settings.map((spot, index) => (
        <PlaceSetting key={index} x={spot.x} y={spot.y} scale={0.82} />
      ))}
    </>
  );
};

const OvalTable = ({ palette, id, seats }) => {
  const chairs = seatsOnEllipse(seats, 69, 43, 50, 30);
  const settings = placeSettingsInRect(seats, 30, 24, 78, 38);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      {chairs.map((chair, index) => (
        <Chair key={index} {...chair} palette={palette} scale={0.92} />
      ))}
      <g filter={`url(#shadow-${id})`}>
        <ellipse cx={69} cy={43} rx={46} ry={24} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <ellipse cx={69} cy={43} rx={46} ry={24} fill={`url(#grain-${id})`} stroke="none" />
      </g>
      {settings.map((spot, index) => (
        <PlaceSetting key={index} x={spot.x} y={spot.y} scale={0.82} />
      ))}
    </>
  );
};

const BoothTable = ({ palette, id, seats }) => {
  const topSeats = Math.ceil(seats / 2);
  const bottomSeats = seats - topSeats;
  const sideSeats = seats > 6 ? Math.max(0, seats - 6) : 0;
  const settings = placeSettingsInRect(seats, 34, 36, 60, 20);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      <Bench x={24} y={6} width={80} palette={palette} seatCount={topSeats} />
      <Bench x={24} y={76} width={80} palette={palette} flip seatCount={bottomSeats} />
      {sideSeats > 0 && (
        <>
          <Chair x={12} y={46} angle={-90} palette={palette} scale={0.88} />
          {sideSeats > 1 && (
            <Chair x={116} y={46} angle={90} palette={palette} scale={0.88} />
          )}
        </>
      )}
      <g filter={`url(#shadow-${id})`}>
        <rect x={30} y={34} width={68} height={24} rx={5} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <rect x={30} y={34} width={68} height={24} rx={5} fill={`url(#grain-${id})`} stroke="none" />
      </g>
      {settings.map((spot, index) => (
        <PlaceSetting key={index} x={spot.x} y={spot.y} scale={0.88} />
      ))}
    </>
  );
};

const BarTable = ({ palette, id, seats }) => {
  const stoolXs = distributeOnLine(seats, 22, 134);

  return (
    <>
      <WoodDefs id={id} palette={palette} />
      {stoolXs.map((x) => (
        <Stool key={x} x={x} y={48} palette={palette} />
      ))}
      <line
        x1={14}
        y1={42}
        x2={142}
        y2={42}
        stroke={palette.seatDark}
        strokeWidth={1.4}
        strokeLinecap="round"
        opacity={0.55}
      />
      <g filter={`url(#shadow-${id})`}>
        <rect x={8} y={12} width={140} height={22} rx={11} fill={`url(#wood-${id})`} stroke={palette.woodEdge} strokeWidth={1.2} />
        <rect x={8} y={12} width={140} height={22} rx={11} fill={`url(#grain-${id})`} stroke="none" />
        <rect x={12} y={15} width={132} height={4} rx={2} fill={palette.woodLight} opacity={0.35} />
      </g>
    </>
  );
};

const SHAPE_RENDERERS = {
  round: RoundTable,
  square: SquareTable,
  rectangle: RectangleTable,
  oval: OvalTable,
  booth: BoothTable,
  bar: BarTable,
};

const TableShapeIllustration = ({
  shape = "round",
  seats,
  status = "available",
  baseColor,
  className = "",
}) => {
  const reactId = useId().replace(/:/g, "");
  const palette = resolvePalette(status, baseColor);
  const Renderer = SHAPE_RENDERERS[shape] || RoundTable;
  const viewBox = VIEWBOX[shape] || VIEWBOX.round;
  const seatCount = normalizeSeats(seats, shape);

  return (
    <svg
      className={`reservation-table-illustration is-${status} ${className}`.trim()}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <Renderer palette={palette} id={reactId} seats={seatCount} />
    </svg>
  );
};

export { DEFAULT_TABLE_COLOR, resolvePalette };
export default TableShapeIllustration;
